import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeb } from '../context/WebContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { FaPhoneAlt, FaWhatsapp, FaShieldAlt, FaClock, FaCheckCircle, FaUserCheck, FaTools, FaEnvelope } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

export default function Home() {
  const { services, testimonials, locations, settings, addEnquiry } = useWeb();
  
  // Set SEO meta tags dynamically
  useDocumentMeta(settings.meta.home.title, settings.meta.home.desc);

  // Background slider state loaded from database settings
  const defaultHeroSlides = [
    {
      title: "Balcony Safety Nets",
      subtitle: "Professional Installation",
      image: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&w=1600&q=80"
    },
    {
      title: "Invisible Grill For Balcony",
      subtitle: "Professional Installation",
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80"
    },
    {
      title: "Pigeon Safety Nets",
      subtitle: "Professional Installation",
      image: "https://images.unsplash.com/photo-1596464716127-f2a82984de30?auto=format&fit=crop&w=1600&q=80"
    },
    {
      title: "All Types Of Hangers",
      subtitle: "Professional Installation",
      image: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1600&q=80"
    },
    {
      title: "Children Safety Nets",
      subtitle: "Professional & Safe",
      image: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&w=1600&q=80"
    },
    {
      title: "Cricket & Sports Nets",
      subtitle: "Heavy-Duty Quality",
      image: "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?auto=format&fit=crop&w=1600&q=80"
    }
  ];

  const heroSlides = settings.heroSlides && settings.heroSlides.length > 0 
    ? settings.heroSlides 
    : defaultHeroSlides;

  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    location: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service || !formData.location) {
      toast.error('Please fill in all required fields (Name, Phone, Service, and Location)');
      return;
    }
    addEnquiry(formData);
    toast.success('Your enquiry has been submitted successfully! Our expert will call you back shortly.');
    setFormData({
      name: '',
      phone: '',
      email: '',
      service: '',
      location: '',
      message: ''
    });
  };

  const handleWhatsappSubmit = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'whatsapp_click', {
        'event_category': 'Engagement',
        'event_label': 'WhatsApp Form Submit'
      });
    }
    const cleanNum = settings.whatsappNumber.replace(/[^0-9]/g, '');
    const text = `Hi Vengamamba Enterprises, My name is ${formData.name || 'Customer'}. I am interested in ${formData.service || 'Safety Nets Installation'} in ${formData.location || 'my location'}. Phone: ${formData.phone || 'N/A'}.`;
    window.open(`https://api.whatsapp.com/send?phone=${cleanNum}&text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="bg-slate-50">
      <Toaster position="top-right" />

      {/* Premium Hero Banner Section - Centered Animated Content with Ken Burns background slider */}
      <section className="relative text-white overflow-hidden min-h-[500px] sm:min-h-[600px] lg:min-h-[680px] flex items-center py-20">
        {/* Background Slider - Animated sliding transition */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <AnimatePresence initial={false}>
            <motion.div
              key={activeSlide}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0 w-full h-full"
            >
              <div
                className="w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url('${heroSlides[activeSlide].image}')` }}
              />
            </motion.div>
          </AnimatePresence>
          {/* Subtle dark gradient overlay from the bottom to ensure text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/60 pointer-events-none z-1"></div>
        </div>

        {/* Background Decorative Grid */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,90,0,0.15),transparent_70%)] pointer-events-none z-1"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none z-1"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full flex flex-col justify-center items-center text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlide}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
              }}
              className="space-y-4 sm:space-y-6 max-w-4xl"
            >
              <motion.div
                variants={{
                  hidden: { y: -20, opacity: 0 },
                  visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } }
                }}
                className="inline-flex items-center space-x-2 bg-[#fdd61f] px-3.5 py-1.5 rounded-full text-xs font-extrabold tracking-wide text-slate-950 uppercase shadow-md mx-auto"
              >
                <FaShieldAlt className="text-xs" />
                <span>{heroSlides[activeSlide].subtitle}</span>
              </motion.div>

              <motion.h1
                variants={{
                  hidden: { y: 40, opacity: 0 },
                  visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 80, damping: 15 } }
                }}
                className="text-3xl sm:text-5xl lg:text-7xl font-black leading-tight text-white px-4"
                style={{
                  textShadow: "2px 2px 4px rgba(0,0,0,0.8), 4px 4px 10px rgba(0,0,0,0.5), 1px 18px 6px rgba(16,16,16,0.4)"
                }}
              >
                {heroSlides[activeSlide].title}
              </motion.h1>

              <motion.p
                variants={{
                  hidden: { opacity: 0 },
                  visible: { opacity: 1, transition: { duration: 0.8 } }
                }}
                className="hidden sm:block text-xs sm:text-base lg:text-lg text-slate-100 font-semibold max-w-2xl mx-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
              >
                Vengamamba Enterprises provides ISO-certified Balcony Nets, Pigeon Nets, Kids Safety Nets, Invisible Grills, and Sports Nets. Experienced installation across Hyderabad, Bangalore, Chennai, Vijayawada, and Visakhapatnam.
              </motion.p>

              <motion.div
                variants={{
                  hidden: { y: 30, opacity: 0 },
                  visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } }
                }}
                className="flex flex-col sm:flex-row justify-center gap-3 pt-2"
              >
                <a 
                  href={`tel:${settings.phone1}`}
                  onClick={() => gtag_report_conversion()}
                  className="flex items-center justify-center space-x-2.5 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 sm:py-3.5 sm:px-8 rounded-xl transition shadow-lg shadow-orange-500/20 text-xs sm:text-sm cursor-pointer"
                >
                  <FaPhoneAlt />
                  <span>Call Now: {settings.phone1}</span>
                </a>
                <button 
                  onClick={handleWhatsappSubmit}
                  className="flex items-center justify-center space-x-2.5 bg-green-600 hover:bg-green-700 text-white font-bold px-6 py-3 sm:py-3.5 sm:px-8 rounded-xl transition shadow-lg shadow-green-600/20 text-xs sm:text-sm cursor-pointer"
                >
                  <FaWhatsapp className="text-lg" />
                  <span>WhatsApp Enquiry</span>
                </button>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slide Indicators / Counter sync */}
        <div className="absolute bottom-6 left-0 right-0 z-20 flex justify-center items-center gap-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveSlide(index)}
              className="group flex items-center space-x-1.5 focus:outline-none cursor-pointer"
            >
              <span className={`text-[10px] sm:text-xs font-black transition-colors ${
                activeSlide === index ? 'text-[#fdd61f]' : 'text-white/60 group-hover:text-white'
              }`}>
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className={`h-1 rounded-full transition-all duration-300 ${
                activeSlide === index ? 'w-8 bg-[#fdd61f]' : 'w-2 bg-white/40 group-hover:bg-white/70'
              }`} />
            </button>
          ))}
        </div>
      </section>

      {/* Horizontal High-Conversion Quick Enquiry Bar */}
      <section className="bg-orange-500 py-6 relative z-20 shadow-lg border-b border-orange-600/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-white text-center lg:text-left">
              <h3 className="text-lg sm:text-xl font-black">Get Free Quote & Site Visit</h3>
              <p className="text-xs text-orange-100">Submit details for a free measurement check.</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap items-center gap-3 w-full lg:w-auto">
              <input 
                type="text" 
                placeholder="Your Name *" 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full lg:w-40 bg-white text-slate-900 placeholder-slate-400 border-none outline-none rounded-xl px-4 py-2.5 text-xs focus:ring-2 focus:ring-white transition"
                required
              />
              <input 
                type="tel" 
                placeholder="Phone Number *" 
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full lg:w-40 bg-white text-slate-900 placeholder-slate-400 border-none outline-none rounded-xl px-4 py-2.5 text-xs focus:ring-2 focus:ring-white transition"
                required
              />
              <select
                value={formData.service}
                onChange={(e) => setFormData({...formData, service: e.target.value})}
                className="w-full lg:w-44 bg-white text-slate-900 border-none outline-none rounded-xl px-3 py-2.5 text-xs focus:ring-2 focus:ring-white transition"
                required
              >
                <option value="" disabled className="text-slate-400">Select Net Type</option>
                {services.map(s => (
                  <option key={s.id} value={s.title}>{s.title}</option>
                ))}
              </select>
              <select
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full lg:w-44 bg-white text-slate-900 border-none outline-none rounded-xl px-3 py-2.5 text-xs focus:ring-2 focus:ring-white transition"
                required
              >
                <option value="" disabled className="text-slate-400">Select City</option>
                {locations.map(l => (
                  <option key={l.id} value={l.name}>{l.name}</option>
                ))}
              </select>
              <button 
                type="submit" 
                className="w-full lg:w-auto bg-slate-900 hover:bg-slate-800 text-white font-extrabold px-6 py-2.5 rounded-xl transition shadow-md text-xs whitespace-nowrap cursor-pointer"
              >
                Get Callback
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-16 overflow-hidden">
          <motion.span 
            initial={{ opacity: 0, scale: 0.8, rotate: -2 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100, delay: 0.1 }}
            className="inline-block text-xs uppercase bg-gradient-to-r from-orange-500/10 to-amber-500/10 text-orange-600 font-extrabold px-4 py-1.5 rounded-full border border-orange-200/50 tracking-wider shadow-sm"
          >
            ★ Our Installation Services
          </motion.span>

          <h2 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight flex flex-wrap justify-center gap-x-3 gap-y-1 overflow-hidden py-2 max-w-4xl mx-auto">
            {"Safety Net Solutions We Specialize In".split(" ").map((word, idx) => (
              <motion.span
                key={idx}
                initial={{ y: "100%", opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.06, ease: [0.2, 0.65, 0.3, 0.9] }}
                className="inline-block"
              >
                {word === "Solutions" || word === "Specialize" ? (
                  <span className="bg-gradient-to-r from-orange-600 to-amber-500 bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(249,115,22,0.15)]">{word}</span>
                ) : word}
              </motion.span>
            ))}
          </h2>

          <motion.p 
            initial={{ opacity: 0, y: 15, filter: "blur(4px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-slate-500 max-w-2xl mx-auto text-sm sm:text-base font-medium leading-relaxed"
          >
            Select a service below to view pricing guidelines, key features, and book certified expert installation.
          </motion.p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-8"
        >
          {services.map((service, index) => (
            <motion.div 
              key={service.id} 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.08 }}
              className="relative h-[260px] sm:h-[430px] rounded-2xl sm:rounded-3xl overflow-hidden group shadow-lg hover:shadow-2xl hover:shadow-orange-500/10 border border-slate-100 transition-all duration-500 flex flex-col justify-end"
            >
              {/* Background Image */}
              <img 
                src={service.image} 
                alt={service.title} 
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
              />

              {/* Dynamic Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent z-10 transition-all duration-300" />
              
              {/* Colored accent glow on top edge when hovered */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-amber-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-30" />

              {/* Top Details (Price and Index Number) */}
              <div className="absolute top-3 left-3 right-3 sm:top-6 sm:left-6 sm:right-6 flex justify-between items-center z-20">
                <span className="text-lg sm:text-3xl font-black text-white/20 group-hover:text-orange-500/30 transition-colors font-mono">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="bg-orange-600/90 text-white font-bold backdrop-blur-md px-2 py-1 sm:px-3.5 sm:py-1.5 rounded-full text-[9px] sm:text-xs border border-orange-500/30 shadow-md">
                  {service.priceGuideline}
                </span>
              </div>

              {/* Card Content Area */}
              <div className="relative z-20 p-3 sm:p-6 flex flex-col h-full justify-end">
                {/* Scrollable / Expandable Info Container */}
                <div className="space-y-1.5 sm:space-y-3">
                  <h3 className="text-sm sm:text-2xl font-black text-white tracking-tight group-hover:text-orange-400 transition-colors">
                    {service.title}
                  </h3>
                  
                  {/* Short Description - Truncates initially, expands on hover */}
                  <p className="text-slate-300 text-[10px] sm:text-sm leading-relaxed line-clamp-2 group-hover:line-clamp-none transition-all duration-500">
                    {service.shortDesc}
                  </p>

                  {/* Hidden Key Benefits - Slides/Fades in on Hover */}
                  <div className="max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 overflow-hidden transition-all duration-500 ease-out space-y-2 pt-2 border-t border-white/10">
                    {service.benefits && service.benefits.slice(0, 2).map((benefit, bIndex) => (
                      <div key={bIndex} className="flex items-center space-x-2 text-xs text-slate-300">
                        <FaCheckCircle className="text-orange-500 flex-shrink-0" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer Buttons - Appears on Hover */}
                <div className="max-h-0 opacity-0 group-hover:max-h-16 group-hover:opacity-100 overflow-hidden transition-all duration-500 ease-out pt-4 mt-4 flex items-center justify-between border-t border-white/10">
                  <Link 
                    to={`/services/${service.id}`} 
                    className="text-sm font-bold text-white hover:text-orange-400 transition flex items-center group/link"
                  >
                    <span>View Pricing</span>
                    <span className="ml-1.5 group-hover/link:translate-x-1 transition-transform">→</span>
                  </Link>
                  <button 
                    onClick={() => {
                      setFormData({
                        ...formData,
                        service: service.title
                      });
                      document.getElementById('enquiry-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-xs font-bold bg-white text-slate-900 px-4 py-2.5 rounded-xl hover:bg-orange-500 hover:text-white transition-all duration-300 cursor-pointer shadow-lg"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16">
            <span className="text-xs uppercase bg-slate-800 text-orange-500 font-extrabold px-3 py-1 rounded-full border border-slate-700">
              Why Choose Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Delivering Unmatched Quality Standards
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-sm">
              Why thousands of families and businesses trust Vengamamba Enterprises for their safety needs.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            <div className="bg-slate-850 p-4 sm:p-6 rounded-2xl border border-slate-800 space-y-3 sm:space-y-4 hover:border-orange-500/25 transition">
              <div className="bg-orange-600/10 text-orange-500 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-lg sm:text-xl">
                <FaShieldAlt />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-white">ISO Certified Materials</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                We use exclusively high-density, UV-stabilized copolymer nets and marine-grade stainless steel cables that last longer.
              </p>
            </div>

            <div className="bg-slate-850 p-4 sm:p-6 rounded-2xl border border-slate-800 space-y-3 sm:space-y-4 hover:border-orange-500/25 transition">
              <div className="bg-orange-600/10 text-orange-500 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-lg sm:text-xl">
                <FaTools />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-white">Expert Installers</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Our technicians are extensively trained in rope access and high-rise apartment safety regulations.
              </p>
            </div>

            <div className="bg-slate-850 p-4 sm:p-6 rounded-2xl border border-slate-800 space-y-3 sm:space-y-4 hover:border-orange-500/25 transition">
              <div className="bg-orange-600/10 text-orange-500 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-lg sm:text-xl">
                <FaClock />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-white">3-Hour Fast Service</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Call us and get immediate measurements and rapid same-day installation across major cities.
              </p>
            </div>

            <div className="bg-slate-850 p-4 sm:p-6 rounded-2xl border border-slate-800 space-y-3 sm:space-y-4 hover:border-orange-500/25 transition">
              <div className="bg-orange-600/10 text-orange-500 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-lg sm:text-xl">
                <FaUserCheck />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-white">5-Year Warranty</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Enjoy complete peace of mind with our standard product warranty on color fading, netting knots, and rust.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials Section */}
      <section className="py-12 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-3 mb-10 sm:mb-16">
          <span className="text-xs uppercase bg-orange-55 text-orange-600 font-extrabold px-3 py-1 rounded-full border border-orange-200">
            Client Testimonials
          </span>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900">
            What Our Customers Say
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-xs sm:text-sm">
            Read positive feedback from homeowners and commercial property managers.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
          {testimonials.map((test) => (
            <div 
              key={test.id} 
              className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between"
            >
              <div className="space-y-3 sm:space-y-4">
                <div className="flex text-amber-400 text-[10px] sm:text-sm">
                  {Array.from({ length: test.rating }).map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-slate-600 text-xs sm:text-sm italic leading-relaxed">
                  "{test.review}"
                </p>
              </div>
              <div className="pt-3 sm:pt-6 border-t border-slate-100 mt-4 sm:mt-6">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900">{test.name}</h4>
                <p className="text-slate-400 text-[10px] sm:text-xs">{test.location}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 bg-slate-100 border-t border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div className="space-y-3">
            <span className="text-xs uppercase bg-orange-50 text-orange-600 font-extrabold px-3 py-1 rounded-full border border-orange-200">
              Active Service Locations
            </span>
            <h2 className="text-3xl font-extrabold text-slate-900">
              We Install Across Major Cities & Surrounding Regions
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-sm">
              We have local execution teams operating continuously in:
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {locations.filter(l => l.active).map((loc) => (
              <div 
                key={loc.id} 
                className="bg-white border border-slate-200/80 rounded-2xl px-6 py-4 flex items-center space-x-3.5 shadow-sm hover:border-orange-500/20 transition-all hover:scale-105"
              >
                <div className="w-8 h-8 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center">
                  📍
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-slate-900 text-sm">{loc.name}</h4>
                  <a href={`tel:${loc.phone}`} onClick={() => gtag_report_conversion()} className="text-xs text-orange-600 font-semibold hover:underline">
                    {loc.phone}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Contact & Enquiry Form Section */}
      <section id="enquiry-section" className="py-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 text-white rounded-3xl overflow-hidden shadow-2xl grid grid-cols-1 lg:grid-cols-12">
          {/* Contact Details Panel */}
          <div className="lg:col-span-5 bg-gradient-to-br from-slate-950 to-slate-900 p-8 sm:p-10 space-y-8 flex flex-col justify-between border-r border-slate-800">
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-white">Contact Info</h3>
              <p className="text-slate-400 text-xs sm:text-sm">
                Have questions or need an instant custom measurement quote? Contact our safety net helpline directly.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-slate-800 text-orange-500 p-3 rounded-lg mt-0.5">
                  <FaPhoneAlt />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase">Phone Support</h4>
                  <a href={`tel:${settings.phone1}`} onClick={() => gtag_report_conversion()} className="text-white text-sm font-semibold hover:text-orange-500 transition">{settings.phone1}</a>
                  <p className="text-xs text-slate-500">Toll Free / Site Head</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-slate-800 text-orange-500 p-3 rounded-lg mt-0.5">
                  <FaPhoneAlt />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase">Alternative Hotline</h4>
                  <a href={`tel:${settings.phone2}`} onClick={() => gtag_report_conversion()} className="text-white text-sm font-semibold hover:text-orange-500 transition">{settings.phone2}</a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-slate-800 text-orange-500 p-3 rounded-lg mt-0.5">
                  <FaEnvelope className="text-orange-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase">Email Enquiries</h4>
                  <a href={`mailto:${settings.email}`} className="text-white text-sm font-semibold hover:text-orange-500 transition break-all">{settings.email}</a>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-800 text-xs text-slate-500">
              Vengamamba Enterprises - Active in all major residential apartments, IT parks, and villa communities.
            </div>
          </div>

          {/* Enquiry Form Panel */}
          <div className="lg:col-span-7 p-8 sm:p-10">
            <h3 className="text-2xl font-bold text-white mb-6">Send an Enquiry</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Your Name *</label>
                  <input 
                    type="text" 
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-orange-500 outline-none transition"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Phone Number *</label>
                  <input 
                    type="tel" 
                    placeholder="10 Digit Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-orange-500 outline-none transition"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Required Net *</label>
                  <select
                    value={formData.service}
                    onChange={(e) => setFormData({...formData, service: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-3 text-sm text-white focus:border-orange-500 outline-none transition"
                    required
                  >
                    <option value="" disabled>Select Net Type</option>
                    {services.map(s => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">City / Location *</label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-3 text-sm text-white focus:border-orange-500 outline-none transition"
                    required
                  >
                    <option value="" disabled>Select City</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.name}>{l.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Additional Requirements</label>
                <textarea 
                  rows="3" 
                  placeholder="E.g., measurements in sq.ft, balcony dimensions..."
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-orange-500 outline-none transition resize-none"
                ></textarea>
              </div>

              <div className="flex gap-4 pt-2">
                <button 
                  type="submit" 
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl transition text-center shadow-lg cursor-pointer"
                >
                  Submit Form
                </button>
                <button 
                  type="button"
                  onClick={handleWhatsappSubmit}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold px-6 py-3.5 rounded-xl transition flex items-center justify-center"
                  title="Submit via WhatsApp"
                >
                  <FaWhatsapp className="text-xl" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
